import { FormEventHandler, useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FieldErrors,
  SubmitHandler,
  UseFormReturn,
  useForm,
} from "react-hook-form";
import { isErrorMessage } from "@sscs/types";
import { Toast } from "@sscs/ui";
import * as yup from "Utils/yup";
import { useSharedReportLoginMutation } from "../services/sharedReports.api";

type ReportPasswordFormData = {
  password: string;
};

type UseReportPasswordProtection = {
  errors: FieldErrors<ReportPasswordFormData>;
  isAuthError: boolean;
  isSubmitSuccessful: boolean;
  methods: UseFormReturn<ReportPasswordFormData>;
  onSubmit: FormEventHandler<HTMLFormElement>;
};

const schema: yup.SchemaOf<ReportPasswordFormData> = yup.object({
  password: yup
    .string()
    .required()
    .min(8, "Password must be at least 8 characters"),
});

const useReportPasswordProtection = (
  token: string,
  onSuccessfulLogin: () => void,
): UseReportPasswordProtection => {
  const [hasError, setHasError] = useState(false);

  const defaultValues = useMemo(
    () => ({
      password: "",
    }),
    [],
  );

  const [loginToSharedReport, { isError }] = useSharedReportLoginMutation();

  const methods = useForm<ReportPasswordFormData>({
    resolver: yupResolver(schema),
    mode: "onSubmit",
    defaultValues,
  });

  const {
    formState: { errors, isSubmitSuccessful, isDirty },
    reset,
    clearErrors,
    handleSubmit,
  } = methods;

  useEffect(() => {
    setHasError(isError);

    // resetting the form after submit in this way will clear out errors for revalidation next time
    // without losing the field values
    if (isSubmitSuccessful && isError) {
      reset(defaultValues, { keepValues: true });
    }
  }, [isSubmitSuccessful, reset, defaultValues, isError]);

  const onSubmit: SubmitHandler<ReportPasswordFormData> = async (data) => {
    try {
      await loginToSharedReport({
        token_key: token,
        password: data.password,
      }).unwrap();

      onSuccessfulLogin();
    } catch (error) {
      clearErrors();
      setHasError(false);

      Toast.show({
        title: isErrorMessage(error) ? error.data.error : "Invalid password",
        type: "error",
      });
    }
  };

  return {
    errors,
    isAuthError: hasError && !isDirty,
    isSubmitSuccessful,
    methods,
    onSubmit: handleSubmit(onSubmit),
  };
};

export default useReportPasswordProtection;
